import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { oids } from "@/configs/Settings";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { resolveCrmOrganizationId } from "src-next/modules/crm-clinical/communications/helpers/resolveCrmOrganizationId";
import { useCommunicationsWhatsAppSendDialog } from "src-next/modules/crm-clinical/communications/hooks/useCommunicationsWhatsAppSendDialog";
import { useCrmSetupConfiguration } from "src-next/modules/crm-clinical/setup/hooks/useCrmSetupConfiguration";
import { crmMessageFranchiseStatsService } from "src-next/modules/crm-clinical/statistics/services/crmMessageFranchiseStatsService";

import { sendPostAttendanceFluxmedFollowUp } from "../helpers/sendPostAttendanceFluxmedFollowUp";

export type PostAttendanceFollowUpType = "medication" | "exam";

export interface PostAttendancePatientData {
  id: string;
  name: string;
  phone?: string;
  cpf?: string;
}

export interface OpenPostAttendanceFollowUpParams {
  row: any;
  type: PostAttendanceFollowUpType;
  patientData?: PostAttendancePatientData;
  /** Bundle de ServiceRequest — usado no envio direto Fluxmed de exames. */
  exames?: any;
}

export interface UsePostAttendanceFollowUpSendDialogParams {
  /** Executado após envio bem-sucedido (ex.: marcar notificationSent no FHIR). */
  onFollowUpSent?: (params: {
    row: any;
    type: PostAttendanceFollowUpType;
  }) => void | Promise<void>;
}

async function hasFranchiseMessagesForSend(
  organizationId: string,
): Promise<boolean> {
  const data =
    await crmMessageFranchiseStatsService.fetchCrmMessageFranchiseUsageData(
      organizationId,
    );

  const fromStats = data.stats.remainingMessagesForSend;
  if (typeof fromStats === "number" && Number.isFinite(fromStats)) {
    return fromStats > 0;
  }

  const available = data.stats.totalMessagesAvailable ?? 0;
  const used = data.stats.totalMessagesUsed ?? 0;
  return Math.max(0, available - used) > 0;
}

/**
 * Pós-atendimento:
 * - Meta e Fluxmed padrão → mesma API de flows (`flow_medication` / `flow_exame`).
 * - Fluxmed sem saldo no contrato CRM → modal somente de compra de pacotes.
 */
export function usePostAttendanceFollowUpSendDialog({
  onFollowUpSent,
}: UsePostAttendanceFollowUpSendDialogParams = {}) {
  const { access, user } = useSessionContext();
  const { organizationId: contextOrganizationId } = useOrganizationContext();

  const effectiveOrganizationId = useMemo(
    () =>
      resolveCrmOrganizationId(
        contextOrganizationId,
        access?.organizationId,
      ),
    [contextOrganizationId, access?.organizationId],
  );

  const practitionerId = useMemo(
    () => (user?.username ? `${oids.cpf}-${user.username}` : undefined),
    [user?.username],
  );

  const crmSetupConfiguration = useCrmSetupConfiguration({
    organizationId: effectiveOrganizationId,
  });

  const channelOption =
    crmSetupConfiguration.configurationStatus?.selectedOption ?? null;
  const isMetaChannel = channelOption === "metaEmbeddedSignup";
  const isFluxmedChannel =
    channelOption === "fluxmedDefault" || channelOption == null;
  const hasActiveMetaEndpoint =
    isMetaChannel &&
    Boolean(
      crmSetupConfiguration.configurationStatus?.metaConnection?.wabaId?.trim(),
    );

  const [isSendingFollowUp, setIsSendingFollowUp] = useState(false);

  const pendingFollowUpRef = useRef<{
    row: any;
    type: PostAttendanceFollowUpType;
  } | null>(null);

  const handleMessageSent = useCallback(() => {
    const pending = pendingFollowUpRef.current;
    if (!pending) return;

    pendingFollowUpRef.current = null;
    void onFollowUpSent?.(pending);
  }, [onFollowUpSent]);

  const { openSendMessage, sendDialogProps } = useCommunicationsWhatsAppSendDialog(
    {
      organizationId: effectiveOrganizationId,
      practitionerId,
      onMessageSent: handleMessageSent,
      franchiseRefreshSignal: 0,
      hasActiveMetaEndpoint,
    },
  );

  /** Abre o dialog somente de compra quando o contrato CRM não tem mensagens. */
  const openPurchaseDialog = useCallback(
    (params: OpenPostAttendanceFollowUpParams) => {
      const { row, type } = params;

      pendingFollowUpRef.current = { row, type };

      void openSendMessage({ purchasePackagesOnly: true });
    },
    [openSendMessage],
  );

  const executePostAttendanceFlowSend = useCallback(
    async (params: OpenPostAttendanceFollowUpParams): Promise<boolean> => {
      const { row, type, exames } = params;

      await sendPostAttendanceFluxmedFollowUp({ row, type, exames });
      toast.success("Acompanhamento enviado com sucesso");

      const pending = pendingFollowUpRef.current;
      pendingFollowUpRef.current = null;
      if (pending) {
        await onFollowUpSent?.(pending);
      }

      return true;
    },
    [onFollowUpSent],
  );

  const sendFollowUp = useCallback(
    async (params: OpenPostAttendanceFollowUpParams) => {
      const { row, type, patientData, exames } = params;

      if (!patientData?.id?.trim()) {
        toast.warn("Selecione um paciente com dados válidos para enviar.");
        return false;
      }

      if (!effectiveOrganizationId) {
        toast.warn("Organização não identificada para envio.");
        return false;
      }

      pendingFollowUpRef.current = { row, type };

      // Fluxmed padrão: verifica saldo do contrato CRM antes do envio.
      if (!isMetaChannel) {
        let hasMessages = true;
        try {
          hasMessages = await hasFranchiseMessagesForSend(
            effectiveOrganizationId,
          );
        } catch {
          // Falha na API de franquia não deve impedir o envio.
          hasMessages = true;
        }

        if (!hasMessages) {
          openPurchaseDialog(params);
          return true;
        }
      }

      // Meta e Fluxmed usam a mesma API de flows (templates flow_medication / flow_exame).
      setIsSendingFollowUp(true);
      try {
        return await executePostAttendanceFlowSend(params);
      } catch (error) {
        pendingFollowUpRef.current = null;
        toast.error(
          error instanceof Error
            ? error.message
            : "Erro ao enviar acompanhamento. Tente novamente.",
        );
        return false;
      } finally {
        setIsSendingFollowUp(false);
      }
    },
    [
      effectiveOrganizationId,
      executePostAttendanceFlowSend,
      isMetaChannel,
      openPurchaseDialog,
    ],
  );

  return {
    sendFollowUp,
    /** @deprecated use sendFollowUp */
    openFollowUpSend: sendFollowUp,
    sendDialogProps,
    isSendingFollowUp,
    isMetaChannel,
    isFluxmedChannel,
  };
}
